/**
 * useSendMessageV2 — Conversations V2 send pipeline (Option Z).
 *
 * Replaces the previous adapter that wrapped V1's useMessageSending. Built on
 * TanStack Query v5's canonical offline pattern:
 *  - setMutationDefaults registered ONCE at app init (App.tsx) → survives unmount,
 *    enables mutation queue persistence + resume on reload
 *  - scope: { id: conversationId } → serial per-conversation, parallel across
 *  - PersistQueryClientProvider (in App.tsx) → mutation queue persists to localStorage
 *  - client_message_id idempotency → server-side dedup via save_tenant_message_v2
 *
 * Fixes 9 V1 bugs (see docs/mods/conversations-v2/SEND_PIPELINE_V2.md):
 *  1. Composer never blocks (no `sending` flag exported)
 *  2. No blocking name lookup (uses AuthContext.userProfile.full_name synchronously)
 *  3. Honest delivery_status (stays 'pending' until WhatsApp confirms)
 *  4. No setTimeout reload (cache writes only)
 *  5. client_message_id idempotency (server-side via RPC)
 *  6. Offline persistence (PersistQueryClientProvider in App.tsx)
 *  7. Realtime/poll dedup via metadata.client_message_id (in useConversationMessages flatten)
 *  8. Per-conversation serial ordering (TanStack scope)
 *  9. WhatsApp-side dedup (Option A guards in whatsapp-send-* edge functions)
 *
 * Preserves V1's good behaviors:
 *  - Auto user assignment (assign_user_to_conversation, fire-and-forget)
 *  - Auto escalation resolution (resolve-escalation, fire-and-forget)
 *  - AI suggestion data → senderType derivation
 *  - Reply context metadata (metadata.context.id)
 *  - Media file routing (whatsapp-send-media vs whatsapp-send-optimized)
 *  - Selected model ID storage in metadata
 */

import { useCallback } from 'react';
import {
  type QueryClient,
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  classifyWhatsAppError,
  toClassifiableWhatsAppError,
  type WhatsAppFailureClass,
} from '@/lib/whatsapp-error-classifier';
import type {
  Message,
  ConversationListItem,
} from '../types/conversation-types';
import {
  patchConversationInListCache,
  buildSendMessagePatch,
  buildAssignmentPatch,
  buildEscalationResolvedPatch,
} from './conversationListCache';

// ============================================================================
// Types
// ============================================================================

interface MediaFile {
  mediaUrl?: string;
  type?: 'image' | 'video' | 'audio' | 'document';
  file?: { name?: string; size?: number; type?: string };
}

interface SendVariables {
  clientMessageId: string;
  tenantSchema: string;
  tenantId: string;
  conversationId: string;
  customerId: string | null;
  customerPhone: string | undefined;
  senderUuid: string;
  senderId: string;
  senderType: string;
  content: string;
  messageType: string;
  metadata: Record<string, unknown> | null;
  mediaFiles?: MediaFile[];
  replyToWhatsappMessageId?: string;
  modelId?: string;
  // Distinct from `senderId` (a single collapsed name-or-email-or-id display string) — these two
  // are the sender's OWN, separately-typed name and email, needed to patch the list cache
  // correctly once the fire-and-forget auto-assign side effect below resolves (item 6 gap found
  // by an independent audit: the list cache used to go stale on assignment/escalation-resolve
  // triggered from a send, not only from the explicit ContextPanel/CustomerHeader controls).
  senderName: string;
  senderEmail: string | null;
}

/** A send of this conversation that ended in error and is still waiting for the operator to decide. */
export interface FailedSend {
  clientMessageId: string;
  content: string;
  messageType: string;
  failedAt: number;
  failure: WhatsAppFailureClass;
}

interface UseSendMessageV2Options {
  conversationId: string;
  customerId: string;
  customerPhone?: string;
  tenantSchema: string;
}

// Query keys (must match useConversationMessages.ts and useConversationList.ts)
const MESSAGES_KEY = 'conv-messages-v2';
const LIST_KEY = 'conv-list-v2';
const SEND_MUTATION_KEY = ['send-message-v2'] as const;

// ============================================================================
// Helpers
// ============================================================================

function makeOptimisticMessage(v: SendVariables): Message {
  // Build a Message-shaped row matching the discriminated union.
  // We use `as unknown as Message` because the runtime shape is correct but
  // the TS discriminated union requires per-type field constraints.
  const base = {
    id: v.clientMessageId, // optimistic ID === clientMessageId for cache match
    conversation_id: v.conversationId,
    sender_type: v.senderType,
    sender_id: v.senderId,
    sender_uuid: v.senderUuid,
    external_id: null,
    is_read: false,
    created_at: new Date().toISOString(),
    delivery_status: 'pending' as const,
    ai_generated: null,
    ai_model_used: v.modelId ?? null,
    ai_confidence_score: null,
    metadata: { ...(v.metadata ?? {}), client_message_id: v.clientMessageId },
    message_type: v.messageType,
    content: v.content,
    media_url: v.mediaFiles?.[0]?.mediaUrl ?? null,
    media_type: v.mediaFiles?.[0]?.type ?? null,
    media_caption: null,
  };
  return base as unknown as Message;
}

function buildSenderType(aiSuggestionData?: Record<string, unknown>): string {
  if (!aiSuggestionData) return 'human_agent';
  const t = aiSuggestionData.senderType;
  return typeof t === 'string' ? t : 'ai_with_human_review_no_changes';
}

function buildMetadata(
  aiSuggestionData: Record<string, unknown> | undefined,
  modelId: string | undefined,
  replyToWhatsappMessageId: string | undefined,
  mediaFile: MediaFile | undefined,
): Record<string, unknown> | null {
  const md: Record<string, unknown> = {};
  if (aiSuggestionData) Object.assign(md, aiSuggestionData);
  if (modelId) md.originalModelId = modelId;
  if (replyToWhatsappMessageId) md.context = { id: replyToWhatsappMessageId };
  if (mediaFile?.file) {
    md.original_filename = mediaFile.file.name;
    md.file_size = mediaFile.file.size;
    md.mime_type = mediaFile.file.type;
    md.media_url = mediaFile.mediaUrl;
  }
  return Object.keys(md).length ? md : null;
}

// ============================================================================
// executeSend — the mutation function (runs in background, survives unmount)
// ============================================================================

export async function executeSend(variables: SendVariables, queryClient: QueryClient): Promise<Message> {
  // 1. Idempotent server save via new RPC. Returns existing row if a previous
  //    attempt already saved this client_message_id.
  // Param names follow .claude/rules/database.md "NO p_ prefix" rule.
  const { data: savedRow, error: saveError } = await supabase.rpc('save_tenant_message_v2', {
    tenant_schema: variables.tenantSchema,
    conversation_id: variables.conversationId,
    sender_type: variables.senderType,
    sender_id: variables.senderId,
    sender_uuid: variables.senderUuid,
    content: variables.content,
    message_type: variables.messageType,
    metadata: variables.metadata,
    media_url: variables.mediaFiles?.[0]?.mediaUrl ?? null,
    client_message_id: variables.clientMessageId,
  });

  if (saveError) throw saveError;
  const serverRow = savedRow as Message & { id: string; external_id: string | null };

  // 2. WhatsApp delivery (only if customer conversation). Skip if Option A
  //    guard inside the edge function will detect an existing external_id.
  //
  //    Every `waError` (a thrown `FunctionsHttpError`) is unwrapped through
  //    `toClassifiableWhatsAppError` before it leaves this function — that is the ONE
  //    place this happens. `onError`, `retry` and `useMutationState`'s `select` below all
  //    read this same already-unwrapped error and classify it with the plain,
  //    synchronous `classifyWhatsAppError`; none of them re-reads the response body.
  if (variables.customerId && variables.customerPhone) {
    const isMedia = !!variables.mediaFiles?.length && !!variables.mediaFiles[0]?.mediaUrl;
    if (isMedia) {
      const mediaFile = variables.mediaFiles![0];
      const { data: waResult, error: waError } = await supabase.functions.invoke('whatsapp-send-media', {
        body: {
          tenantId: variables.tenantId,
          conversationId: variables.conversationId,
          customerId: variables.customerId,
          messageId: serverRow.id,
          messageType: mediaFile.type,
          mediaUrl: mediaFile.mediaUrl,
          fileName: mediaFile.file?.name,
          mimeType: mediaFile.file?.type,
          recipientPhone: variables.customerPhone,
          senderType: 'agent',
          senderId: variables.senderUuid,
          modelId: variables.modelId,
          replyToWhatsappMessageId: variables.replyToWhatsappMessageId,
        },
      });
      if (waError) throw await toClassifiableWhatsAppError(waError);
      if (waResult?.error) throw waResult.error;
    } else {
      const { data: waResult, error: waError } = await supabase.functions.invoke('whatsapp-send-optimized', {
        body: {
          conversationId: variables.conversationId,
          content: variables.content,
          messageType: variables.messageType,
          messageId: serverRow.id,
          recipientPhone: variables.customerPhone,
          replyToWhatsappMessageId: variables.replyToWhatsappMessageId,
        },
      });
      if (waError) throw await toClassifiableWhatsAppError(waError);
      if (waResult?.error) throw waResult.error;
    }
  }

  // 3. Fire-and-forget side effects (V1 parity: last-touch-wins assignment +
  //    auto escalation resolve). Errors swallowed — non-critical.
  //
  //    Both patch the list cache once they resolve — an independent audit found that a human
  //    message from an unassigned, escalated conversation reassigns AND resolves the escalation
  //    server-side, but neither used to be reflected in the cache, so the row could keep showing
  //    the old assignee and stay in the Escalated band until the next poll.
  //
  //    assign_user_to_conversation is UNCONDITIONAL (last-touch-wins: the underlying UPDATE sets
  //    assigned_user_id/name/email with no previous-value guard — confirmed by reading
  //    20260108000003_update_assign_rpc_with_display_fields.sql), so every successful call really
  //    did change the row: patching on success alone is accurate.
  //
  //    resolve-escalation is NOT unconditional — its own UPDATE only matches rows still
  //    escalation_status = 'pending' (WHERE id = $1 AND escalation_status = 'pending') and returns
  //    success either way, with `rows_updated` telling the two cases apart. Patching on success
  //    alone here would be wrong: EVERY human send calls this fire-and-forget, so a blind patch
  //    would mark every conversation "resolved" regardless of whether it was ever escalated.
  const isHumanInteraction =
    variables.senderType === 'human_agent' ||
    variables.senderType.startsWith('ai_with_human_review');

  if (isHumanInteraction && variables.senderUuid) {
    void supabase
      .rpc('get_user_tenant_schema')
      .then(({ data: schema }) => {
        if (!schema) return;
        void supabase
          .rpc('assign_user_to_conversation', {
            tenant_schema: schema,
            conversation_id: variables.conversationId,
            user_id: variables.senderUuid,
          })
          .then(({ error }) => {
            if (error) return;
            patchConversationInListCache(
              queryClient,
              variables.tenantSchema,
              variables.conversationId,
              buildAssignmentPatch(variables.senderUuid, variables.senderName, variables.senderEmail),
            );
          })
          .catch(() => undefined);
        void supabase.functions
          .invoke('resolve-escalation', {
            body: { tenant_schema: schema, conversation_id: variables.conversationId },
          })
          .then(({ data, error }) => {
            if (error) return;
            const rowsUpdated = (data as { rows_updated?: number } | null)?.rows_updated ?? 0;
            if (rowsUpdated <= 0) return; // was already resolved / not pending — nothing to patch
            patchConversationInListCache(
              queryClient,
              variables.tenantSchema,
              variables.conversationId,
              buildEscalationResolvedPatch(),
            );
          })
          .catch(() => undefined);
      })
      .catch(() => undefined);
  }

  // The serverRow's external_id may have been updated by the edge
  // function via save_tenant_message_external_id (existing RPC). Re-fetch
  // is unnecessary — the next poll picks it up.
  return serverRow;
}

// ============================================================================
// Cache helpers
// ============================================================================

type MessagesCacheData = {
  pages: Message[][];
  pageParams: unknown[];
};

/**
 * PREFIX of the message-thread cache key, not the whole key — and that difference is the whole
 * point of this function existing.
 *
 * WHAT IT COST. useConversationMessages builds
 * `[MESSAGES_KEY, tenantSchema, customerId ?? conversationId, includeTest]`; the fourth element
 * arrived with 846a7591 ("the transcript hid the assistant's reply and called it silence") and this
 * file was not touched. It kept building a THREE-element key, and `setQueryData` matches keys
 * exactly. So from that commit onward every write this mutation made — the optimistic row on
 * mutate, the canonical row on success, and the `delivery_status: 'failed'` mark on error — landed
 * in a cache entry that no component reads. Nothing failed; the screen simply never showed a
 * message that had not yet come back from the server, and a message that failed left no trace at
 * all. An independent UX judge photographed exactly that on 2026-09-08 and called it the only one
 * of thirty-five states where the operator loses a customer and cannot find out.
 *
 * Using the prefix with `setQueriesData` (which matches by prefix) makes the class of bug
 * impossible rather than fixed once: a fifth key element tomorrow cannot silently disconnect the
 * writer from the reader again.
 */
function getMessagesKeyPrefix(tenantSchema: string, customerId: string | null, conversationId: string) {
  return [MESSAGES_KEY, tenantSchema, customerId ?? conversationId] as const;
}

/** Applies `fn` to every message-cache entry for this thread, whatever its trailing key parts. */
function patchMessageCaches(
  queryClient: QueryClient,
  prefix: readonly unknown[],
  fn: (old: MessagesCacheData | undefined) => MessagesCacheData | undefined,
) {
  queryClient.setQueriesData<MessagesCacheData>({ queryKey: prefix }, fn);
}

// ============================================================================
// registerSendMessageV2Defaults — called ONCE from App.tsx
// ============================================================================

export function registerSendMessageV2Defaults(queryClient: QueryClient) {
  queryClient.setMutationDefaults(SEND_MUTATION_KEY, {
    mutationFn: (variables) => executeSend(variables as SendVariables, queryClient),

    // NOTE: `scope` is intentionally NOT set anywhere — neither here nor in the
    // useMutation call site. Sends run in PARALLEL (Slack/WhatsApp Web pattern).
    //
    // Why: Each whatsapp-send-optimized call takes ~3.3s (Meta API roundtrip
    // dominates). Serial scope made 13 rapid sends take ~52s wall-clock. Parallel
    // brings that to ~3-4s — a 12x UX win.
    //
    // Order guarantee comes from 3 layers (none requiring client-side serialization):
    //   1. Optimistic UI: synchronous onMutate writes rows in mutate() call order
    //   2. Server-side INSERT: created_at set at row-insert time, monotonic
    //   3. Meta API: per-recipient FIFO processing on Meta's side (typical, not contractual)
    //
    // This matches Slack, WhatsApp Web, Discord, iMessage, Telegram. The 0.1%
    // theoretical reorder risk only manifests for sub-100ms scripted rapid-fire,
    // not real human typing. Server-side client_message_id idempotency + Option A
    // guards in the edge functions still prevent any duplicate delivery.
    //
    // If production reports order issues, rollback options: (A) parallel + 200ms
    // debounce/batch, (B) optimize edge function internals, (C) hybrid concurrency
    // limit. See plan file for details.

    // Optimistic insert (synchronous, runs inside mutate() before any network)
    onMutate: async (variables) => {
      const v = variables as SendVariables;
      const messagesKey = getMessagesKeyPrefix(v.tenantSchema, v.customerId, v.conversationId);

      // Cancel any in-flight refetch so it can't clobber our optimistic write
      await queryClient.cancelQueries({ queryKey: messagesKey });

      const optimisticRow = makeOptimisticMessage(v);

      patchMessageCaches(queryClient, messagesKey, (old) => {
        if (!old?.pages?.length) {
          return { pages: [[optimisticRow]], pageParams: [null] };
        }
        const firstPage = [optimisticRow, ...old.pages[0]];
        return { ...old, pages: [firstPage, ...old.pages.slice(1)] };
      });

      return { messagesKey, clientMessageId: v.clientMessageId };
    },

    // Server confirmed — replace the optimistic row with the canonical row
    onSuccess: (data, variables, context) => {
      const v = variables as SendVariables;
      const ctx = context as { messagesKey: readonly unknown[]; clientMessageId: string } | undefined;
      if (!ctx) return;
      const serverRow = data as Message;

      patchMessageCaches(queryClient, ctx.messagesKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.map((m) => (m.id === ctx.clientMessageId ? serverRow : m)),
          ),
        };
      });

      // Item 6: patch the row in place instead of invalidating the whole list. An invalidate
      // both fires a network round trip and (before the stream buffer existed) reordered the
      // list at the exact moment the operator was looking at the row they just acted on. See
      // conversationListCache.ts's own doc comment and this repository's progress log for the
      // patch-completeness detector this patch was checked against before landing.
      patchConversationInListCache(
        queryClient,
        v.tenantSchema,
        v.conversationId,
        buildSendMessagePatch(serverRow),
      );
    },

    onError: (error, variables, context) => {
      const v = variables as SendVariables;
      const ctx = context as { messagesKey: readonly unknown[]; clientMessageId: string } | undefined;
      if (!ctx) return;

      const failure = classifyWhatsAppError(error);

      // Mark the optimistic row as failed in the cache. This alone is NOT what keeps the message
      // on screen: the thread is a polled query, so the next poll replaces `pages` wholesale and
      // an optimistic row the server never saw is gone. What keeps it is that the errored mutation
      // itself survives (TanStack keeps it in the mutation cache, and App.tsx persists that queue),
      // and ChatPanel renders those as failed bubbles — see `failedSends` below. This write is
      // still worth doing so the row reads "failed" instead of "pending" in the window before the
      // next poll.
      patchMessageCaches(queryClient, ctx.messagesKey, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) =>
            page.map((m) =>
              m.id === ctx.clientMessageId
                ? { ...m, delivery_status: 'failed' as const }
                : m,
            ),
          ),
        };
      });

      // window_closed → flip the conversation row to template_only across all
      // conv-list-v2 cache entries (regardless of active filters) so the
      // composer's existing template-mode banner fires immediately.
      if (failure.kind === 'window_closed') {
        queryClient.setQueriesData<{ pages: ConversationListItem[][]; pageParams: unknown[] }>(
          { queryKey: [LIST_KEY, v.tenantSchema] },
          (old) => {
            if (!old?.pages) return old;
            return {
              ...old,
              pages: old.pages.map((page) =>
                page.map((c) =>
                  c.id === v.conversationId ? { ...c, status: 'template_only' as const } : c,
                ),
              ),
            };
          },
        );
      }
    },

    // Auto-retry only for transient classes; classifier decides
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      const cls = classifyWhatsAppError(error);
      return cls.autoRetryable;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}

// ============================================================================
// useSendMessageV2 — per-component hook
// ============================================================================

export function useSendMessageV2({
  conversationId,
  customerId,
  customerPhone,
  tenantSchema,
}: UseSendMessageV2Options) {
  const { user, userProfile, tenantId } = useAuth();
  const queryClient = useQueryClient();

  // Per-conversation mutation key (matches the SEND_MUTATION_KEY prefix so the
  // global defaults apply, but enables per-conversation useMutationState filtering)
  const mutationKey = ['send-message-v2', conversationId] as const;

  // No `scope` — sends run in parallel (Slack/WhatsApp Web pattern).
  // See the comment in registerSendMessageV2Defaults for full rationale.
  // The generics are stated rather than inferred because the mutationFn lives in
  // setMutationDefaults (App.tsx), so this call site has nothing to infer `variables` from and
  // TypeScript falls back to `void` — which made every `mutate(variables)` here a type error and
  // stopped protecting the one place where the shape actually matters: the retry below, which
  // re-sends variables it read back out of the mutation cache.
  const mutation = useMutation<Message, unknown, SendVariables>({
    mutationKey,
  });

  // Every send of THIS conversation that ended in error, with the text the operator wrote and the
  // variables needed to send it again. This is what makes a failed message survive: the thread is a
  // polled query and its cache is replaced on every poll, but an errored mutation stays in the
  // mutation cache (and in the persisted queue) until someone retries or discards it.
  const failedSends = useMutationState({
    filters: { mutationKey: ['send-message-v2'], status: 'error' },
    select: (m) => {
      const vars = m.state.variables as SendVariables | undefined;
      if (!vars || vars.conversationId !== conversationId) return null;
      return {
        clientMessageId: vars.clientMessageId,
        content: vars.content,
        messageType: vars.messageType,
        failedAt: m.state.submittedAt,
        failure: classifyWhatsAppError(m.state.error),
      } satisfies FailedSend;
    },
  }).filter((f): f is FailedSend => f !== null);

  // The one shown in the banner above the thread: the most recent failure. Same source as the
  // bubbles, so the banner cannot outlive the message it is talking about — which is what used to
  // happen (`clearError` was a documented no-op, so the strip stayed on screen over conversations
  // that were fine, and over no conversation at all).
  const liveError: WhatsAppFailureClass | null =
    failedSends.length > 0
      ? failedSends.reduce((a, b) => (b.failedAt > a.failedAt ? b : a)).failure
      : null;

  /** Drops errored sends from the mutation cache, so the bubble and the banner both go away. */
  const forgetFailed = useCallback(
    (predicate: (vars: SendVariables) => boolean) => {
      const cache = queryClient.getMutationCache();
      for (const m of cache.getAll()) {
        if (m.state.status !== 'error') continue;
        const vars = m.state.variables as SendVariables | undefined;
        if (!vars || !predicate(vars)) continue;
        // remove() notifies the cache's subscribers itself (query-core 5.96 MutationCache.remove),
        // so useMutationState re-renders and the bubble disappears with no extra nudge.
        cache.remove(m);
      }
    },
    [queryClient],
  );

  /**
   * Returns whether the send was ACCEPTED — i.e. whether a mutation now exists carrying this text.
   * The composer clears its box and its saved draft on the strength of that answer and nothing
   * else: the two early returns below used to drop the operator's text on the floor with a
   * console warning nobody sees, and the composer cleared anyway.
   */
  const sendMessage = useCallback(
    (
      content: string,
      messageType: string = 'text',
      modelId?: string,
      mediaFiles?: MediaFile[],
      aiSuggestionData?: Record<string, unknown>,
      replyToWhatsappMessageId?: string,
    ): boolean => {
      if (!user || !userProfile) {
        if (import.meta.env.DEV) console.warn('[useSendMessageV2] no user/profile, skipping send');
        return false;
      }
      if (!content.trim() && !mediaFiles?.length) return false;

      const senderType = buildSenderType(aiSuggestionData);
      const senderId =
        userProfile.full_name ||
        (userProfile as { email?: string }).email ||
        user.id;

      const variables: SendVariables = {
        clientMessageId: crypto.randomUUID(),
        tenantSchema,
        tenantId: tenantId ?? '',
        conversationId,
        customerId: customerId || null,
        customerPhone,
        senderUuid: user.id,
        senderId,
        senderType,
        content: content.trim() || (mediaFiles?.[0]?.file?.name ?? ''),
        messageType,
        metadata: buildMetadata(aiSuggestionData, modelId, replyToWhatsappMessageId, mediaFiles?.[0]),
        mediaFiles,
        replyToWhatsappMessageId,
        modelId,
        senderName: userProfile.full_name || senderId,
        senderEmail: userProfile.email || null,
      };

      mutation.mutate(variables);
      return true;
    },
    [user, userProfile, tenantId, tenantSchema, conversationId, customerId, customerPhone, mutation],
  );

  /**
   * Sends a failed message again, REUSING ITS clientMessageId — which is what makes this safe to
   * put in front of an operator who will press it twice. `save_tenant_message_v2` is idempotent on
   * that key and returns the existing row instead of inserting a second one, and the
   * whatsapp-send-* functions refuse to deliver a message that already carries an external_id. A
   * retry that minted a fresh id would have neither guard and would send the customer the same
   * message twice.
   */
  const retrySend = useCallback(
    (clientMessageId: string) => {
      const cache = queryClient.getMutationCache();
      const target = cache
        .getAll()
        .find(
          (m) =>
            m.state.status === 'error' &&
            (m.state.variables as SendVariables | undefined)?.clientMessageId === clientMessageId,
        );
      if (!target) return;
      const variables = target.state.variables as SendVariables;
      cache.remove(target);
      mutation.mutate(variables);
    },
    [queryClient, mutation],
  );

  /** Throws a failed message away on purpose. The operator decided; nothing is sent. */
  const discardSend = useCallback(
    (clientMessageId: string) => {
      forgetFailed((vars) => vars.clientMessageId === clientMessageId);
    },
    [forgetFailed],
  );

  /** Dismisses the banner by forgetting every failed send of THIS conversation. */
  const clearError = useCallback(() => {
    forgetFailed((vars) => vars.conversationId === conversationId);
  }, [forgetFailed, conversationId]);

  return {
    sendMessage,
    liveError,
    clearError,
    failedSends,
    retrySend,
    discardSend,
  };
}
